/**
 * PURE. The skull profile (creatures §B7.2 variants as ring-radius profiles + deformers), shared by the skeleton builder
 * (to seat the eye, lid and brow pivots exactly on the face) and the head mesh. Raw (pre-normalization) units.
 */
import type { CreatureSpec } from '../dna/resolve';

export interface SkullDims { chin: number; hH: number; headZ: number; skullRx: number; skullRz: number; faceZ: number; sz: number }

/** Base round-skull profile: fraction of head height → [side mult, front mult]. */
const F = [0, 0.1, 0.26, 0.45, 0.63, 0.8, 0.93, 1];
const MX = [0.66, 0.88, 1.0, 1, 0.97, 0.9, 0.7, 0.0];
const MZ = [0.62, 0.86, 0.97, 1, 0.98, 0.9, 0.7, 0.0];

const interp = (xs: readonly number[], ys: readonly number[], x: number): number => {
  if (x <= xs[0]) return ys[0];
  for (let i = 1; i < xs.length; i++) if (x <= xs[i]) { const t = (x - xs[i - 1]) / (xs[i] - xs[i - 1]); return ys[i - 1] + (ys[i] - ys[i - 1]) * t; }
  return ys[ys.length - 1];
};

export interface SkullProfile {
  /** Ring at fraction f of the head height: side radius, front radius, centre z, y. */
  ring(f: number): { rs: number; rf: number; cz: number; y: number };
  /** Angular shape multiplier of a ring (θ in turns; 0/0.5 = sides, 0.25 = front). */
  shape(f: number, theta: number): number;
  /** Front-surface z at (x, y). */
  front(x: number, y: number): number;
  /** Flat-face plane (human skull) or null. */
  facePlane: number | null;
  /** Fractions of the crown cap tip height (onion domes rise). */
  crownLift: number;
}

export function skullProfile(spec: CreatureSpec, d: SkullDims): SkullProfile {
  const sk = spec.o.skull;
  const cheek = spec.v.cheekFluff;
  const jowls = spec.v.jowls;
  const human = sk === 'human';
  const rzBase = human ? d.skullRz * 1.22 : d.skullRz;
  const facePlane = human ? d.faceZ : null;
  const mods = (f: number): [number, number, number] => {
    let mx = interp(F, MX, f), mz = interp(F, MZ, f);
    let dz = 0.006 * (0.5 - f);
    switch (sk) {
      case 'egg': mx *= 1 - 0.22 * f; mz *= 1 - 0.08 * f; break;
      case 'pear': mx *= f < 0.6 ? 1.2 - 0.35 * f : 0.99; mz *= 1.06 - 0.1 * f; break;
      case 'pancake': mx *= 1.1 + (f > 0.6 ? 0.25 * (f - 0.6) : 0); mz *= 0.9; break;
      case 'wedge': mx *= 1.02 - 0.08 * f; dz += 0.01 * (1 - f); break;
      case 'boxy': mx = Math.min(1, mx * 1.12); mz = Math.min(1, mz * 1.1); break;
      case 'onion': if (f > 0.55) mx *= 1 - 0.55 * (f - 0.55) / 0.45; break;
      case 'long': mx *= 0.86; mz *= 1.06; dz += 0.018 * (1 - f) * (1 - f); break;
      case 'lumpy': mx *= 1.02; break;
      default: break;
    }
    if (f < 0.5) mx *= 1 + 0.24 * cheek * Math.max(0, 1 - Math.abs(f - 0.24) / 0.24);
    if (f < 0.25) { mx *= 1 + 0.32 * jowls * (1 - f / 0.25); mz *= 1 + 0.12 * jowls; }
    return [mx, mz, dz];
  };
  const ring = (f: number): { rs: number; rf: number; cz: number; y: number } => {
    const [mx, mz, dz] = mods(f);
    return { rs: d.skullRx * mx, rf: rzBase * mz, cz: d.headZ + dz * d.sz, y: d.chin + f * d.hH - (f < 0.12 ? 0.03 * jowls * (1 - f / 0.12) : 0) };
  };
  const shape = (f: number, theta: number): number => {
    const c = Math.cos(theta * Math.PI * 2), s = Math.sin(theta * Math.PI * 2);
    let k = 1;
    if (sk === 'boxy') { const p = 4; k = 1 / Math.pow(Math.pow(Math.abs(c), p) + Math.pow(Math.abs(s), p), 1 / p); k = 1 + (k - 1) * 0.6; }
    if (sk === 'wedge' && s > 0) k *= 1 - 0.22 * s * s * (1 - f);
    if (sk === 'lumpy') k *= 1 + 0.05 * Math.sin(theta * 3 * Math.PI * 2 + f * 7) * Math.sin(f * Math.PI);
    return k;
  };
  const front = (x: number, y: number): number => {
    const f = Math.max(0, Math.min(0.999, (y - d.chin) / d.hH));
    const r = ring(f);
    const t = Math.max(0, 1 - (x * x) / (r.rs * r.rs));
    let z = r.cz + r.rf * Math.sqrt(t) * shape(f, 0.25);
    if (facePlane !== null) z = flatten(z, f, facePlane);
    return z;
  };
  return { ring, shape, front, facePlane, crownLift: sk === 'onion' ? 0.1 : sk === 'boxy' ? -0.02 : 0 };
}

/** Human flat face (art §5.4): the front beyond the face plane is pressed to it, softly. */
export function flatten(z: number, f: number, plane: number): number {
  if (z <= plane) return z;
  const w = f < 0.06 ? f / 0.06 : f > 0.86 ? Math.max(0, 1 - (f - 0.86) / 0.1) : 1;
  return z - (z - plane) * 0.85 * w;
}
